import React from 'react';
import { connect, Dispatch } from 'react-redux';
import { Action as ReduxAction } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ConnectedExplorerToolbar } from '~src/spacesPlatform/components/Explorer/ConnectedExplorerToolbar';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { getContentSpaceDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { SpaceDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsTypes';
import { SpaceInfoItemType } from '../../models/ContentType';
import { ISpaceItemDetailInfo } from '../../models/ISpaceItemDetailInfo';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { SpotlightAction, SpotlightActionButtons } from './SpotlightActionButtons';
import { useHeaderTelemetry } from './UseHeaderTelemetryHook';

export function SpaceHeader<C extends SpaceInfoItemType>(props: SpaceHeaderProps<C>): JSX.Element {
  useHeaderTelemetry();

  const headerContentRef = React.useRef<HTMLDivElement>(null);
  const [allInSameRow, setAllInSameRow] = React.useState(true);
  React.useEffect(() => {
    if (headerContentRef.current) {
      const height = headerContentRef.current.clientHeight;
      setAllInSameRow(height <= 80);
    }
  }, []);

  if (!props.item) {
    return null;
  }

  const item: ISpaceItemDetailInfo<C> = props.item;
  const customContainerClass = props.containerClassName || '';
  const showBasicName = _.isUndefined(props.nameElement);
  const nameElement =
    showBasicName ?
      <h1 data-tb-test-id={SpaceHeaderTestIds.name} className={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.TitleV2 : SpaceHeaderStyles.Title} style={{ flex: FeatureFlags.compactHeaderEnabled ? '0 0 auto' : '1 0 auto' }}>{item.name}</h1> :
      props.nameElement;

  let layout;
  if (FeatureFlags.compactHeaderEnabled) {
    layout =
      <div>
        <div className={SpaceHeaderStyles.RowV2 + ' ' + SpaceHeaderStyles.CompactHeader} data-tb-test-id={`tb-${item.contentType}-space-header-${item.id}`} style={{ display: allInSameRow ? 'flex' : 'block' }}>
          <div ref={headerContentRef} className={SpaceHeaderStyles.RowNowrap} style={{ position: 'relative' }}>
            {!props.hideHeaderIcon && renderSpaceIcon(item)}
            <div className={SpaceHeaderStyles.CapWidth}>
              <div className={SpaceHeaderStyles.RowV2}>
                {nameElement}
                <div className={`${SpaceHeaderStyles.RowNowrap} ${customContainerClass}`}>
                  <section className={SpaceHeaderStyles.DetailIcons}>
                    {props.detailIcons}
                  </section>
                  <section className={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.MetadataV2 : SpaceHeaderStyles.Metadata}>
                    {props.metadataElement}
                  </section>
                </div>
              </div>
              {props.descriptionElement}
            </div>
          </div>
          {
            props.showToolbar && <div className={SpaceHeaderStyles.Grow}>
              <ConnectedExplorerToolbar skipHorizontalMargin={true} />
            </div>
          }
          {props.spotlightActions &&
            <SpotlightActionButtons actions={props.spotlightActions}
              item={item}
              availableActions={props.availableActions}
              dispatchSpotlightAction={props.dispatchSpotlightAction} />
          }
          {props.bottomElement}
        </div>
      </div>;
  }
  else {
    layout =
      <div>
        <div className={SpaceHeaderStyles.Row}
          data-tb-test-id={`tb-${item.contentType}-space-header-${item.id}`}>
          {!props.hideHeaderIcon && renderSpaceIcon(item)}
          <div className={`${SpaceHeaderStyles.ColumnFlexGrow} ${customContainerClass}`}>
            <div className={SpaceHeaderStyles.RowAlignedCenter}>
              {nameElement}
              {props.detailIcons}
            </div>
            <div className={SpaceHeaderStyles.MetadataContainer}>
              <section data-tb-test-id={SpaceHeaderTestIds.metadata} className={SpaceHeaderStyles.Metadata}>{props.metadataElement}</section>
            </div>
            {props.descriptionElement}
          </div>
        </div>
        {props.spotlightActions &&
          <SpotlightActionButtons actions={props.spotlightActions}
            item={item}
            availableActions={props.availableActions}
            dispatchSpotlightAction={props.dispatchSpotlightAction} />
        }
        {props.bottomElement}
      </div>;
  }

  return layout;
}

function renderSpaceIcon<C extends SpaceInfoItemType>(item: ISpaceItemDetailInfo<C>): JSX.Element {

  const spaceDefinition = getContentSpaceDefinition(item.contentType) as unknown as SpaceDefinition<C>;
  const iconNode = spaceDefinition.headerIconNode?.(item);

  return (
    <div data-tb-test-id={SpaceHeaderTestIds.icon} className={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.IconDivWrapperV2 : SpaceHeaderStyles.IconDivWrapper}>
      {iconNode ?? <img src={spaceDefinition.headerIconPath(item)}
        alt={''}
        className={SpaceHeaderStyles.Icon} />}
    </div>
  );
}

export interface SpaceHeaderDispatchProps {
  readonly dispatchSpotlightAction?: (reduxAction: ReduxAction) => void;
}

export interface SpaceHeaderOwnProps<C extends SpaceInfoItemType> {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly availableActions?: AvailableActions; // Required only if there are spotlight actions

  // if nameElement is undefined, this dipslays the item name as text. Otherwise, uses nameElement
  // to display the item name.
  readonly nameElement?: JSX.Element;
  readonly hideHeaderIcon?: boolean;

  readonly showToolbar?: boolean;

  // These elements are the variable portions of the header that can be specified differently
  // for each space type
  readonly detailIcons: JSX.Element | null;
  readonly metadataElement: JSX.Element;
  readonly descriptionElement?: JSX.Element | null;
  readonly spotlightActions?: SpotlightAction<C>[];
  readonly bottomElement?: JSX.Element;
  readonly containerClassName?: string;
}

export type SpaceHeaderProps<C extends SpaceInfoItemType> = SpaceHeaderDispatchProps & SpaceHeaderOwnProps<C>;

function mapDispatchToProps(dispatch: Dispatch<AppState>): SpaceHeaderDispatchProps {
  return {
    dispatchSpotlightAction: dispatch
  };
}

export const ConnectedSpaceHeader = connect(null, mapDispatchToProps)(SpaceHeader);

export namespace SpaceHeaderTestIds {
  export const name = 'space-header-name';
  export const ownerName = 'space-header-owner';
  export const hiddenOwnerName = 'space-header-hidden-owner';
  export const description = 'space-header-description';
  export const modifiedAt = 'space-header-modified-at';
  export const certifiedBy = 'space-header-certified-by';
  export const lastRefreshed = 'space-header-last-refreshed';
  export const readMore = 'space-header-read-more';
  export const dataQuality = 'space-header-data-quality';
  export const username = 'space-header-user-name';
  export const siterole = 'space-header-site-role';
  export const lastSignedIn = 'space-header-last-signed-in';
  export const email = 'space-header-email';
  export const dataStatus = 'space-header-data-status';
  export const runNextAt = 'space-header-run-next-at';
  export const domain = 'space-header-domain';
  export const datasource = 'space-datasource';
  export const tags = 'space-header-tags';
  export const icon = 'space-header-icon';
  export const metadata = 'space-header-metadata';
}
